import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Search, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import userService from '../../services/user.service';
import { AuditEntry } from '../../types';

const actionBadge: Record<string, string> = {
  INSERT: 'badge-green',
  UPDATE: 'badge-blue',
  DELETE: 'badge-red',
};

const AuditLogPage = () => {
  const [logs,    setLogs]    = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  // Filters
  const [tableFilter,  setTableFilter]  = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate,     setFromDate]     = useState('');
  const [toDate,       setToDate]       = useState('');
  const [limit,        setLimit]        = useState(100);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await userService.getAuditLog({
        table_name: tableFilter  || undefined,
        action:     actionFilter || undefined,
        from_date:  fromDate     || undefined,
        to_date:    toDate       || undefined,
        limit,
      });
      setLogs(data);
    } catch {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, [tableFilter, actionFilter, fromDate, toDate, limit]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const toggleExpand = (id: number) => {
    setExpanded(prev => prev === id ? null : id);
  };

  return (
    <div className="page-container">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Complete history of all data changes
          </p>
        </div>
        <button onClick={loadLogs} className="btn-secondary">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && <div className="alert-error mb-4"><span>{error}</span></div>}

      {/* Filters */}
      <div className="card mb-4">
        <div className="p-4 flex flex-wrap gap-3">

          {/* Table name filter */}
          <div className="relative flex-1 min-w-40">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="form-input pl-9"
              placeholder="Filter by table name..."
              value={tableFilter}
              onChange={e => setTableFilter(e.target.value)}
            />
          </div>

          {/* Action filter */}
          <select
            className="form-select w-36"
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
          >
            <option value="">All Actions</option>
            <option value="INSERT">INSERT</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </select>

          {/* Date range */}
          <input
            type="date"
            className="form-input w-40"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            title="From date"
          />
          <input
            type="date"
            className="form-input w-40"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            title="To date"
          />

          {/* Limit */}
          <select
            className="form-select w-28"
            value={limit}
            onChange={e => setLimit(parseInt(e.target.value))}
          >
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={250}>250 rows</option>
            <option value={500}>500 rows</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500 mb-3">
        Showing {logs.length} entries
      </p>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <ClipboardList size={40} className="mx-auto mb-3 opacity-40" />
              <p>No audit entries found</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Table</th>
                  <th>Action</th>
                  <th>Changed By</th>
                  <th>IP Address</th>
                  <th>Timestamp</th>
                  <th className="text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <React.Fragment key={log.audit_id}>
                    <tr>
                      <td className="text-gray-400 text-xs">{log.audit_id}</td>
                      <td>
                        <span className="font-mono text-xs bg-gray-100
                                         text-gray-700 px-2 py-0.5 rounded">
                          {log.table_name}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${actionBadge[log.action] || 'badge-gray'}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>
                        <p className="text-sm font-medium text-gray-700">
                          {log.changed_by_name}
                        </p>
                        <p className="text-xs text-gray-400">{log.changed_by_type}</p>
                      </td>
                      <td className="text-xs text-gray-500 font-mono">
                        {log.ip_address || '—'}
                      </td>
                      <td className="text-xs text-gray-500">
                        {new Date(log.changed_at).toLocaleString('en-IN', {
                          day:    '2-digit',
                          month:  'short',
                          year:   'numeric',
                          hour:   '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </td>
                      <td className="text-center">
                        <button
                          onClick={() => toggleExpand(log.audit_id)}
                          className="p-1 text-gray-400 hover:text-blue-600
                                     hover:bg-blue-50 rounded transition-colors"
                        >
                          {expanded === log.audit_id
                            ? <ChevronUp size={14} />
                            : <ChevronDown size={14} />
                          }
                        </button>
                      </td>
                    </tr>

                    {/* Expanded row — shows old and new values */}
                    {expanded === log.audit_id && (
                      <tr className="bg-blue-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Old values */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500
                                            uppercase mb-2">Before</p>
                              <pre className="text-xs bg-white border border-gray-200
                                              rounded-lg p-3 overflow-auto max-h-40
                                              text-gray-700">
                                {log.old_values
                                  ? JSON.stringify(log.old_values, null, 2)
                                  : 'No previous values (INSERT)'
                                }
                              </pre>
                            </div>
                            {/* New values */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500
                                            uppercase mb-2">After</p>
                              <pre className="text-xs bg-white border border-gray-200
                                              rounded-lg p-3 overflow-auto max-h-40
                                              text-gray-700">
                                {JSON.stringify(log.new_values, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;